import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Target,
  DollarSign,
  AlertTriangle,
  Percent,
  Users,
  UserPlus,
  Calculator,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  RotateCcw,
  Lightbulb,
  ChevronRight,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/data/loanData";

type WizardSection = "Pricing" | "Portfolio" | "Risk";

interface Inputs {
  // Targets
  profitTarget: number; // net profit target
  expenses: number; // operating costs

  // Pricing rules
  monthlyInterestRate: number; // 9%
  minInterestDays: number; // 14
  maxDays: number; // 30

  insuranceRate: number; // 9%
  adminRate: number; // 9%
  processingRate: number; // 3%

  joinFeeAmount: number; // 500
  joinFeeNewOnly: boolean; // true

  // Portfolio plan
  repeatCustomers: number;
  avgRepeatLoan: number;
  avgRepeatDaysToPay: number;

  newCustomers: number;
  avgNewLoan: number;
  avgNewDaysToPay: number;

  // Risk
  defaultRate: number; // 8%
  collectionRate: number; // 90%
}

interface Results {
  plannedPrincipal: number;

  chargedDaysRepeat: number;
  chargedDaysNew: number;

  expectedInterest: number;
  expectedFees: number;
  expectedJoinFees: number;
  expectedCharges: number;
  expectedReceivables: number;

  collectedReceivables: number;
  expectedDefaultLoss: number;

  netProfitEstimate: number; // collected charges - defaults - expenses
  gapToTarget: number;
  isAchievable: boolean;

  // Guidance
  minDisbursementToCoverCosts: number; // (expenses + target) / profitPerKES
  netProfitPerKES: number;
  assumedChargedDays: number;
}

const defaultInputs: Inputs = {
  profitTarget: 100000, // ✅ KES 100,000
  expenses: 0,

  monthlyInterestRate: 9,
  minInterestDays: 14,
  maxDays: 30,

  insuranceRate: 9,
  adminRate: 9,
  processingRate: 3,

  joinFeeAmount: 500,
  joinFeeNewOnly: true,

  repeatCustomers: 0,
  avgRepeatLoan: 0,
  avgRepeatDaysToPay: 30,

  newCustomers: 0,
  avgNewLoan: 0,
  avgNewDaysToPay: 30,

  defaultRate: 8, // ✅
  collectionRate: 90, // ✅
};

function parseNum(val: string): number {
  return parseFloat(val.replace(/,/g, "")) || 0;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function computeChargesForPrincipal({
  principal,
  monthlyInterestRate,
  minInterestDays,
  maxDays,
  insuranceRate,
  adminRate,
  processingRate,
  daysToPay,
}: {
  principal: number;
  monthlyInterestRate: number;
  minInterestDays: number;
  maxDays: number;
  insuranceRate: number;
  adminRate: number;
  processingRate: number;
  daysToPay: number;
}) {
  const chargedDays = clamp(daysToPay, minInterestDays, maxDays);

  // days-based interest (min 14 days, max 30 days)
  const interest = principal * (monthlyInterestRate / 100) * (chargedDays / maxDays);

  // fees on principal (insurance+admin+processing)
  const feesRate = (insuranceRate + adminRate + processingRate) / 100;
  const fees = principal * feesRate;

  return { chargedDays, interest, fees, charges: interest + fees };
}

/**
 * Unit economics: net profit contribution per KES 1 disbursed
 * netProfitPerKES = collectedChargesPerKES - defaultLossPerKES
 */
function profitPerKESDisbursed(inputs: Inputs) {
  const feesRate = (inputs.insuranceRate + inputs.adminRate + inputs.processingRate) / 100;

  // weighted avg days by customer mix; fallback to 30
  const totalCount = (inputs.repeatCustomers || 0) + (inputs.newCustomers || 0);
  const wNew = totalCount ? inputs.newCustomers / totalCount : 0.5;
  const wRepeat = 1 - wNew;

  const avgDays =
    wRepeat * (inputs.avgRepeatDaysToPay || inputs.maxDays) +
    wNew * (inputs.avgNewDaysToPay || inputs.maxDays);

  const chargedDays = clamp(avgDays, inputs.minInterestDays, inputs.maxDays);

  const interestPerKES = (inputs.monthlyInterestRate / 100) * (chargedDays / inputs.maxDays);

  // Join fee only for new customers; spread into per-KES (depends on avgNewLoan)
  const joinFeePerKES =
    inputs.joinFeeNewOnly && inputs.newCustomers > 0 && inputs.avgNewLoan > 0
      ? (inputs.joinFeeAmount / inputs.avgNewLoan) * wNew
      : 0;

  const chargesPerKES = interestPerKES + feesRate + joinFeePerKES;
  const collectedChargesPerKES = chargesPerKES * (inputs.collectionRate / 100);
  const defaultLossPerKES = inputs.defaultRate / 100; // principal loss per 1 KES disbursed

  const netProfitPerKES = collectedChargesPerKES - defaultLossPerKES;

  return { netProfitPerKES, chargedDays };
}

function calculate(inputs: Inputs): Results {
  const repeatPrincipal = inputs.repeatCustomers * inputs.avgRepeatLoan;
  const newPrincipal = inputs.newCustomers * inputs.avgNewLoan;
  const plannedPrincipal = repeatPrincipal + newPrincipal;

  const repeat = computeChargesForPrincipal({
    principal: repeatPrincipal,
    monthlyInterestRate: inputs.monthlyInterestRate,
    minInterestDays: inputs.minInterestDays,
    maxDays: inputs.maxDays,
    insuranceRate: inputs.insuranceRate,
    adminRate: inputs.adminRate,
    processingRate: inputs.processingRate,
    daysToPay: inputs.avgRepeatDaysToPay || inputs.maxDays,
  });

  const fresh = computeChargesForPrincipal({
    principal: newPrincipal,
    monthlyInterestRate: inputs.monthlyInterestRate,
    minInterestDays: inputs.minInterestDays,
    maxDays: inputs.maxDays,
    insuranceRate: inputs.insuranceRate,
    adminRate: inputs.adminRate,
    processingRate: inputs.processingRate,
    daysToPay: inputs.avgNewDaysToPay || inputs.maxDays,
  });

  const expectedInterest = repeat.interest + fresh.interest;
  const expectedFees = repeat.fees + fresh.fees;

  const expectedJoinFees = inputs.joinFeeNewOnly
    ? inputs.newCustomers * inputs.joinFeeAmount
    : (inputs.newCustomers + inputs.repeatCustomers) * inputs.joinFeeAmount;

  const expectedCharges = expectedInterest + expectedFees + expectedJoinFees;
  const expectedReceivables = plannedPrincipal + expectedCharges;

  const collectedReceivables = expectedReceivables * (inputs.collectionRate / 100);

  // Defaults treated as principal loss
  const expectedDefaultLoss = plannedPrincipal * (inputs.defaultRate / 100);

  // Profit comes from collected charges (interest+fees+join)
  const collectedCharges = expectedCharges * (inputs.collectionRate / 100);

  const netProfitEstimate = collectedCharges - expectedDefaultLoss - inputs.expenses;

  const gapToTarget = netProfitEstimate - inputs.profitTarget;
  const isAchievable = gapToTarget >= 0;

  // Guidance: minimum disbursement required to cover expenses + profit target
  const unit = profitPerKESDisbursed(inputs);
  const needed = inputs.expenses + inputs.profitTarget;
  const minDisbursementToCoverCosts = unit.netProfitPerKES > 0 ? needed / unit.netProfitPerKES : 0;

  return {
    plannedPrincipal,

    chargedDaysRepeat: repeat.chargedDays,
    chargedDaysNew: fresh.chargedDays,

    expectedInterest,
    expectedFees,
    expectedJoinFees,
    expectedCharges,
    expectedReceivables,

    collectedReceivables,
    expectedDefaultLoss,

    netProfitEstimate,
    gapToTarget,
    isAchievable,

    minDisbursementToCoverCosts,
    netProfitPerKES: unit.netProfitPerKES,
    assumedChargedDays: unit.chargedDays,
  };
}

function buildRecommendations(inputs: Inputs, r: Results) {
  const recs: { icon: string; type: "warn" | "info" | "success"; text: string }[] = [];

  // Hard guardrail
  if (r.netProfitPerKES <= 0) {
    recs.push({
      icon: "🛑",
      type: "warn",
      text: `Unit economics are negative. At collection ${inputs.collectionRate}% and defaults ${inputs.defaultRate}%, you lose per KES disbursed. Improve collections, reduce defaults, or adjust pricing.`,
    });
    return recs;
  }

  // Minimum disbursement guidance
  if (r.minDisbursementToCoverCosts > 0 && r.plannedPrincipal < r.minDisbursementToCoverCosts) {
    const missing = r.minDisbursementToCoverCosts - r.plannedPrincipal;
    recs.push({
      icon: "📌",
      type: "warn",
      text: `To cover expenses + profit target, you MUST disburse at least KES ${formatCurrency(r.minDisbursementToCoverCosts)}. You are short by KES ${formatCurrency(missing)}.`,
    });
    recs.push({
      icon: "🧠",
      type: "info",
      text: `Options: increase customers/average loan, improve collection above ${inputs.collectionRate}%, reduce default below ${inputs.defaultRate}%, or cut expenses.`,
    });
  }

  if (inputs.collectionRate < 85) {
    recs.push({
      icon: "⚠️",
      type: "warn",
      text: "Collection rate below 85% is risky for monthly targets. Strengthen follow-up, reminders, and branch accountability.",
    });
  }

  if (inputs.defaultRate > 10) {
    recs.push({
      icon: "🛡️",
      type: "warn",
      text: "Default rate above 10% is high. Tighten scoring and shift more volume to repeat borrowers.",
    });
  }

  if (r.isAchievable) {
    recs.push({
      icon: "✅",
      type: "success",
      text: `Plan is achievable. Estimated surplus: KES ${formatCurrency(r.gapToTarget)} above target.`,
    });
  } else {
    recs.push({
      icon: "📉",
      type: "warn",
      text: `Plan is not meeting target. Estimated shortfall: KES ${formatCurrency(Math.abs(r.gapToTarget))}.`,
    });
  }

  return recs;
}

type StepKey = keyof Inputs;

const STEPS: {
  id: number;
  section: WizardSection;
  title: string;
  subtitle: string;
  icon: any;
  bg: string;
  color: string;
  fields: {
    key: StepKey;
    label: string;
    placeholder?: string;
    hint?: string;
  }[];
}[] = [
  {
    id: 1,
    section: "Pricing",
    title: "Interest & Days Rule",
    subtitle: "9% monthly interest, charged by days (min 14 days, max 30)",
    icon: Percent,
    bg: "bg-purple-50",
    color: "text-chart-3",
    fields: [
      { key: "monthlyInterestRate", label: "Monthly Interest Rate (%)", placeholder: "e.g. 9" },
      { key: "minInterestDays", label: "Minimum Interest Days", placeholder: "e.g. 14", hint: "Pay tomorrow → still charge 14 days interest." },
      { key: "maxDays", label: "Max Days (Full Interest)", placeholder: "e.g. 30", hint: "Pay at 30+ days → charge full month." },
    ],
  },
  {
    id: 2,
    section: "Pricing",
    title: "Fees & Join Amount",
    subtitle: "Fees on principal + join fee for new customers",
    icon: DollarSign,
    bg: "bg-blue-50",
    color: "text-chart-2",
    fields: [
      { key: "insuranceRate", label: "Insurance Fee (%)", placeholder: "e.g. 9" },
      { key: "adminRate", label: "Admin Fee (%)", placeholder: "e.g. 9" },
      { key: "processingRate", label: "Processing Fee (%)", placeholder: "e.g. 3" },
      { key: "joinFeeAmount", label: "Join Amount (KES) — New Customers Only", placeholder: "e.g. 500" },
    ],
  },
  {
    id: 3,
    section: "Portfolio",
    title: "Profit Goal",
    subtitle: "Net profit target (after defaults and expenses)",
    icon: Target,
    bg: "bg-accent",
    color: "text-primary",
    fields: [{ key: "profitTarget", label: "Monthly Profit Target (KES)", placeholder: "e.g. 100,000" }],
  },
  {
    id: 4,
    section: "Portfolio",
    title: "Monthly Expenses",
    subtitle: "Operating costs for the month",
    icon: DollarSign,
    bg: "bg-muted",
    color: "text-foreground",
    fields: [{ key: "expenses", label: "Total Monthly Expenses (KES)", placeholder: "e.g. 300,000" }],
  },
  {
    id: 5,
    section: "Portfolio",
    title: "Repeat Loans Plan",
    subtitle: "Repeat customers count, avg loan, avg days to repay",
    icon: Users,
    bg: "bg-emerald-50",
    color: "text-chart-1",
    fields: [
      { key: "repeatCustomers", label: "Repeat Customers (count)", placeholder: "e.g. 200" },
      { key: "avgRepeatLoan", label: "Average Repeat Loan (KES)", placeholder: "e.g. 10,000" },
      { key: "avgRepeatDaysToPay", label: "Avg Days to Repay (repeat)", placeholder: "e.g. 30", hint: "Used for days-based interest." },
    ],
  },
  {
    id: 6,
    section: "Portfolio",
    title: "New Customers Plan",
    subtitle: "New customers count, avg loan, avg days to repay",
    icon: UserPlus,
    bg: "bg-yellow-50",
    color: "text-chart-4",
    fields: [
      { key: "newCustomers", label: "New Customers (count)", placeholder: "e.g. 80" },
      { key: "avgNewLoan", label: "Average New Loan (KES)", placeholder: "e.g. 8,000" },
      { key: "avgNewDaysToPay", label: "Avg Days to Repay (new)", placeholder: "e.g. 20", hint: "Example: 20 days after disbursement → 20 days interest." },
    ],
  },
  {
    id: 7,
    section: "Risk",
    title: "Default Rate",
    subtitle: "Expected principal loss from defaults",
    icon: AlertTriangle,
    bg: "bg-red-50",
    color: "text-destructive",
    fields: [{ key: "defaultRate", label: "Default Rate (%) of principal", placeholder: "e.g. 8" }],
  },
  {
    id: 8,
    section: "Risk",
    title: "Collection Rate",
    subtitle: "How much of receivables you collect this month",
    icon: TrendingUp,
    bg: "bg-muted",
    color: "text-foreground",
    fields: [{ key: "collectionRate", label: "Collection Rate (%)", placeholder: "e.g. 90" }],
  },
];

export default function TargetsPlanner() {
  const [step, setStep] = useState(0); // 0 intro, 1..8 wizard, 9 summary
  const [inputs, setInputs] = useState<Inputs>(defaultInputs);
  const [raw, setRaw] = useState<Record<string, string>>({});

  const totalSteps = STEPS.length;
  const active = STEPS[step - 1];

  const res = useMemo(() => calculate(inputs), [inputs]);
  const recs = useMemo(() => buildRecommendations(inputs, res), [inputs, res]);

  const progress = step === 0 ? 0 : step > totalSteps ? 100 : Math.round((step / totalSteps) * 100);

  function setVal(key: StepKey, v: string) {
    setRaw((p) => ({ ...p, [key]: v }));
    const n = parseNum(v);
    setInputs((p) => ({ ...p, [key]: n as any }));
  }

  function reset() {
    setStep(0);
    setInputs(defaultInputs);
    setRaw({});
  }

  // ── Intro ────────────────────────────────────────────────────────────────
  if (step === 0) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="container mx-auto px-4 py-4 flex items-center gap-3">
            
            <div>
              <h1 className="text-lg font-bold text-foreground">Target Planner</h1>
              <p className="text-xs text-muted-foreground">Days-based interest + fees + risk guidance</p>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-10 max-w-3xl">
          <div className="text-center mb-10">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-accent flex items-center justify-center mb-4">
              <Target className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Monthly Target Creation</h2>
            <p className="text-muted-foreground text-sm max-w-xl mx-auto">
              This planner uses your real pricing:
              <strong> 9% monthly interest</strong> charged by days (min 14), fees (9%+9%+3%),
              join fee KES 500 for new customers, plus default 8% and collection 90%.
            </p>
          </div>

          <div className="grid gap-3 mb-8">
            {[
              { icon: "🧾", label: "Pricing", desc: "Days-based interest + fees + join fee" },
              { icon: "📊", label: "Portfolio", desc: "Repeat & new customers + average days to repay" },
              { icon: "🛡️", label: "Risk", desc: "Default rate + collection rate" },
              { icon: "📌", label: "Guidance", desc: "Shows minimum disbursement required to cover expenses + target" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card">
                <span className="text-2xl">{item.icon}</span>
                <div>
                  <p className="font-semibold text-sm text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
              </div>
            ))}
          </div>

          <Button className="w-full h-12 text-base font-semibold" onClick={() => setStep(1)}>
            Start Planning <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </main>
      </div>
    );
  }

  // ── Summary ──────────────────────────────────────────────────────────────
  if (step > totalSteps) {
    const shortBy =
      res.minDisbursementToCoverCosts > 0 ? Math.max(0, res.minDisbursementToCoverCosts - res.plannedPrincipal) : 0;

    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setStep(totalSteps)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-foreground">Target Summary</h1>
                <p className="text-xs text-muted-foreground">With minimum disbursement guidance</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={reset}>
              <RotateCcw className="h-4 w-4 mr-2" /> Start Over
            </Button>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6 max-w-3xl space-y-6">
          {/* Must disburse banner */}
          <div
            className={cn(
              "rounded-xl p-4 border",
              res.plannedPrincipal >= res.minDisbursementToCoverCosts ? "bg-accent border-primary/30" : "bg-destructive/10 border-destructive/30"
            )}
          >
            <div className="font-bold text-foreground">
              Minimum Disbursement Required: KES {formatCurrency(res.minDisbursementToCoverCosts)}
            </div>
            <div className="text-sm text-muted-foreground">
              Based on expenses (KES {formatCurrency(inputs.expenses)}) + profit target (KES {formatCurrency(inputs.profitTarget)}),
              default {inputs.defaultRate}% and collection {inputs.collectionRate}%. Assumed charged days: {res.assumedChargedDays}.
            </div>
            {shortBy > 0 && (
              <div className="mt-2 text-sm font-semibold text-destructive">
                You are short by KES {formatCurrency(shortBy)}.
              </div>
            )}
          </div>

          {/* Status banner */}
          <div
            className={cn(
              "rounded-xl p-5 flex items-center gap-4 border",
              res.isAchievable ? "bg-accent border-primary/30" : "bg-destructive/10 border-destructive/30"
            )}
          >
            <div className={cn("h-12 w-12 rounded-full flex items-center justify-center shrink-0", res.isAchievable ? "bg-primary" : "bg-destructive")}>
              {res.isAchievable ? (
                <CheckCircle2 className="h-6 w-6 text-primary-foreground" />
              ) : (
                <TrendingDown className="h-6 w-6 text-destructive-foreground" />
              )}
            </div>
            <div>
              <p className="font-bold text-foreground text-lg">{res.isAchievable ? "Target is ACHIEVABLE ✔" : "Shortfall Detected ⚠️"}</p>
              <p className="text-sm text-muted-foreground">
                {res.isAchievable
                  ? `Estimated surplus: KES ${formatCurrency(res.gapToTarget)}`
                  : `Estimated shortfall: KES ${formatCurrency(Math.abs(res.gapToTarget))}`}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-primary" /> Portfolio & Pricing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Planned Principal" value={`KES ${formatCurrency(res.plannedPrincipal)}`} strong />
                <Row label="Interest (monthly)" value={`${inputs.monthlyInterestRate}%`} />
                <Row label="Fees (Ins/Admin/Proc)" value={`${inputs.insuranceRate}% + ${inputs.adminRate}% + ${inputs.processingRate}%`} />
                <Row label="Min/Max interest days" value={`${inputs.minInterestDays} / ${inputs.maxDays}`} />
                <Row label="Charged days (repeat/new)" value={`${res.chargedDaysRepeat} / ${res.chargedDaysNew}`} />
                <Separator className="my-2" />
                <Row label="Expected Interest" value={`KES ${formatCurrency(res.expectedInterest)}`} />
                <Row label="Expected Fees" value={`KES ${formatCurrency(res.expectedFees)}`} />
                <Row label="Expected Join Fees" value={`KES ${formatCurrency(res.expectedJoinFees)}`} />
                <Row label="Total Charges" value={`KES ${formatCurrency(res.expectedCharges)}`} strong />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" /> Risk & Profit
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Collection Rate" value={`${inputs.collectionRate}%`} />
                <Row label="Default Rate" value={`${inputs.defaultRate}%`} />
                <Row label="Expected Default Loss" value={`KES ${formatCurrency(res.expectedDefaultLoss)}`} danger />
                <Row label="Monthly Expenses" value={`KES ${formatCurrency(inputs.expenses)}`} />
                <Separator className="my-2" />
                <Row label="Net Profit Estimate" value={`KES ${formatCurrency(res.netProfitEstimate)}`} strong highlight />
                <Row label="Profit Target" value={`KES ${formatCurrency(inputs.profitTarget)}`} />
                <Row
                  label={res.isAchievable ? "Surplus" : "Shortfall"}
                  value={`KES ${formatCurrency(Math.abs(res.gapToTarget))}`}
                  strong
                  status={res.isAchievable ? "ok" : "bad"}
                />
              </CardContent>
            </Card>
          </div>

          {recs.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-chart-4" /> Guidance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recs.map((r, i) => (
                  <div
                    key={i}
                    className={cn(
                      "flex gap-3 p-3 rounded-lg text-sm border",
                      r.type === "warn"
                        ? "bg-destructive/8 border-destructive/20"
                        : r.type === "success"
                        ? "bg-accent border-primary/20"
                        : "bg-muted border-border"
                    )}
                  >
                    <span className="text-base shrink-0">{r.icon}</span>
                    <p className="text-foreground leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <div className="flex gap-3 pb-6">
            <Button variant="outline" className="flex-1" onClick={reset}>
              <RotateCcw className="h-4 w-4 mr-2" /> New Target
            </Button>
            <Button className="flex-1">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Save Target
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // ── Wizard ────────────────────────────────────────────────────────────────
  const StepIcon = active.icon;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setStep((s) => Math.max(0, s - 1))}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-foreground">
                Step {step} of {totalSteps} <span className="text-xs text-muted-foreground">• {active.section}</span>
              </h1>
              <p className="text-xs text-muted-foreground">{active.title}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {progress}%
          </Badge>
        </div>
        <div className="h-1 bg-muted">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl space-y-4">
        <div className="text-center">
          <div className={cn("mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-3 border border-border", active.bg)}>
            <StepIcon className={cn("h-7 w-7", active.color)} />
          </div>
          <h2 className="text-xl font-bold text-foreground">{active.title}</h2>
          <p className="text-sm text-muted-foreground">{active.subtitle}</p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-5">
            {active.fields.map((f) => (
              <div key={String(f.key)} className="space-y-2">
                <Label className="text-sm font-medium">{f.label}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  className="h-12 text-base font-mono"
                  placeholder={f.placeholder}
                  value={raw[f.key] ?? String((inputs as any)[f.key] ?? "")}
                  onChange={(e) => setVal(f.key, e.target.value)}
                />
                {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Guidance Preview (always shows "must disburse") */}
        <Card className="border-primary/15">
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Guidance Preview</CardTitle>
          </CardHeader>
          <CardContent className="pb-4 space-y-2 text-sm">
            <Row label="Assumed charged days" value={`${res.assumedChargedDays} days`} />
            <Row label="Net profit per KES 1 disbursed" value={`${res.netProfitPerKES.toFixed(4)}`} />
            <Row label="Planned Principal" value={`KES ${formatCurrency(res.plannedPrincipal)}`} />
            <Separator />
            <Row
              label="Minimum required disbursement (Expenses + Target)"
              value={`KES ${formatCurrency(res.minDisbursementToCoverCosts)}`}
              strong
              status={res.plannedPrincipal >= res.minDisbursementToCoverCosts ? "ok" : "bad"}
            />

            {res.plannedPrincipal > 0 &&
              res.minDisbursementToCoverCosts > 0 &&
              res.plannedPrincipal < res.minDisbursementToCoverCosts && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm mt-2">
                  <div className="font-semibold text-destructive">You must disburse more.</div>
                  <div className="text-muted-foreground">
                    Short by KES {formatCurrency(res.minDisbursementToCoverCosts - res.plannedPrincipal)} to cover expenses + target at default{" "}
                    {inputs.defaultRate}% and collection {inputs.collectionRate}%.
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          {step > 1 && (
            <Button variant="outline" className="flex-1" onClick={() => setStep((s) => s - 1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
          <Button className="flex-1 h-12 font-semibold" onClick={() => setStep((s) => s + 1)}>
            {step === totalSteps ? (
              <>
                <Calculator className="h-4 w-4 mr-2" /> View Summary
              </>
            ) : (
              <>
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        <div className="flex items-center justify-center gap-2 pt-2">
          {STEPS.map((s) => (
            <button
              key={s.id}
              onClick={() => setStep(s.id)}
              className={cn("h-2 rounded-full transition-all", step === s.id ? "w-7 bg-primary" : step > s.id ? "w-2 bg-primary/40" : "w-2 bg-border")}
            />
          ))}
        </div>
      </main>
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  highlight,
  danger,
  status,
}: {
  label: string;
  value: string;
  strong?: boolean;
  highlight?: boolean;
  danger?: boolean;
  status?: "ok" | "bad";
}) {
  return (
    <div className={cn("flex items-center justify-between py-1.5", highlight && "bg-accent/40 -mx-3 px-3 rounded")}>
      <span className={cn("text-muted-foreground", strong && "font-semibold text-foreground")}>{label}</span>
      <span
        className={cn(
          "font-mono font-medium text-foreground",
          danger && "text-destructive",
          status === "ok" && "text-primary",
          status === "bad" && "text-destructive",
          strong && "font-semibold"
        )}
      >
        {value}
      </span>
    </div>
  );
}